import React from 'react';
import { css } from 'styled-components';
import { isEmpty } from 'lodash';

// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Components
import Layout from 'src/components/common/theme/Layout';
import { Typo } from 'src/components/common/ui';
import { TertiaryButton, TertiaryButtonProps } from 'src/components/common/ui/button/button';

//helpers
import { checkValue } from 'src/components/common/helper';
import { useRenderImageVideoComponent } from './useRenderImageVideoComponent';

//types
import { articleProps } from '../typings';

const V2FeaturedArticle: React.FC = () => {
  const {
    componentProps: {
      data,
      key,
      id,
      defaultSetting: { site },
    },
  } = useComponentContext();

  const {
    backgroundColor,
    ctaTextColor,
    descriptionTextColor,
    headlineTextColor,
    subHeadlineTextColor,
    tagTextColor,
    tagColor,
    tagBorderColor,
    articleList,
    moduleStyle,
  } = data;

  const article: articleProps = articleList?.[0];

  // Dynamic style overrides
  const moduleCss = () => {
    return `
    &.v2-featured-article {
      ${
        checkValue(backgroundColor)
          ? `.v2-featured-article-details{background-color:${backgroundColor} !important;}`
          : ``
      }
      ${
        checkValue(ctaTextColor)
          ? `.featured-article-read-more-btn{
        color:${ctaTextColor} !important;
        svg {fill:${ctaTextColor} !important}
      }`
          : ``
      }
      ${
        checkValue(descriptionTextColor)
          ? `.featured-article-description{
              color:${descriptionTextColor} !important;
              p,span,h1,h2,h3,h4,h5,h6 {
                color:${descriptionTextColor} !important;
              }
            }`
          : ``
      }
      ${checkValue(headlineTextColor) ? `.featured-article-title{color:${headlineTextColor} !important;}` : ``}
      ${checkValue(subHeadlineTextColor) ? `.featured-article-date{color:${subHeadlineTextColor} !important;}` : ``}
      ${
        checkValue(tagTextColor)
          ? `.v2-featured-article-details-container-top-row .category-pill{
            color:${tagTextColor} !important;
          }`
          : ``
      }
      ${
        checkValue(tagColor)
          ? `.v2-featured-article-details-container-top-row .category-pill{
            background-color:${tagColor} !important;
          }`
          : ``
      }
      ${
        checkValue(tagBorderColor)
          ? `.v2-featured-article-details-container-top-row .category-pill{
            border-color:${tagBorderColor} !important;
          }`
          : ``
      }
      ${
        checkValue(article?.videoPlayControlsIconColor)
          ? `
      .controls .actions button{
        border-color: transparent transparent transparent ${article?.videoPlayControlsIconColor};
      }
      `
          : ``
      }
      ${
        checkValue(article?.closingTextColor)
          ? `
        .v2-featured-article-secondary-text{
          color:${article?.closingTextColor} !important;
        }
      `
          : ``
      }
    }
    `;
  };

  const TertiaryButtonProps: TertiaryButtonProps = {
    tag: 'button',
    exClass: 'featured-article-read-more-btn',
    text: article?.ctaText,
    icon: { name: 'arrow_forward_iOS', className: 'read-more-button-icon' },
    url: article?.ctaUrl,
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'Featured Article Read More',
      eventLabel: article?.ctaText,
    },
  };

  const { renderHtml, images } = useRenderImageVideoComponent({ article, siteData: site, moduleStyle });

  const moduleVariation =
    moduleStyle === 'Brand video' ? 'brand-video' : moduleStyle === 'Brand hero' ? 'brand-hero' : '';
  const catExClass = !checkValue(article?.categoryTags, true) ? 'v2-featured-article-no-category-tags' : '';

  const renderHeadline = () => {
    if (article?.ctaText && article.ctaUrl) {
      return (
        <Typo.LinkText text={article?.headline} url={article.ctaUrl} exClass="featured-article-title override-heading-font-family"/>
        // <a href={article.ctaUrl} className="featured-article-title override-heading-font-family">
        //   {article?.headline}
        // </a>
      );
    } else {
      return (
        <Typo.HTMLText tag="h2" exClass="featured-article-title override-heading-font-family">
          {article?.headline}
        </Typo.HTMLText>
      );
    }
  };

  if (isEmpty(article) && (article?.videoFilePath || article?.video?.[0]?.url || images?.image)) {
    return null;
  }

  return (
    <Layout
      css={css`
        ${moduleCss()}
      `}
      className="v2-container v2-featured-article"
      HtmlTag="section"
      id={checkValue(id) ? id : key?.next()}
    >
      <div className={`v2-featured-article-container ${article?.moduleTextStyle} ${moduleVariation}`}>
        <div className="v2-featured-article-image-container">{renderHtml()}</div>
        <div className="v2-featured-article-details">
          <div className="v2-featured-article-details-container">
            <div className={`v2-featured-article-details-container-top-row ${catExClass}`}>
              {checkValue(article?.categoryTags, true) &&
                article?.categoryTags.map(({ navigationItemText, id }) => (
                  <div className="category-pill" key={id}>
                    {navigationItemText}
                  </div>
                ))}
            </div>
            <div className="v2-featured-article-details-container-middle-row">
              {checkValue(article?.headline) && <>{renderHeadline()}</>}
              {checkValue(article?.subHeadline) && (
                <Typo.HTMLText tag="p" exClass="featured-article-date">
                  {article?.subHeadline}
                </Typo.HTMLText>
              )}
            </div>
            <div className="v2-featured-article-details-container-bottom-row">
              {checkValue(article?.description) && (
                <Typo.HTMLText tag="div" exClass="featured-article-description">
                  {article?.description}
                </Typo.HTMLText>
              )}
              {moduleStyle !== 'Brand hero' && checkValue(article?.ctaText) && checkValue(article?.ctaUrl) && (
                <TertiaryButton {...TertiaryButtonProps} />
              )}
            </div>
            <div className="v2-featured-article-details-container-closing-text-row">
              {moduleStyle === 'Brand hero' && checkValue(article?.closingText) && (
                <Typo.HTMLText tag="div" exClass="v2-featured-article-secondary-text">
                  {article?.closingText}
                </Typo.HTMLText>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default V2FeaturedArticle;
